import { useEffect, useRef, useState } from "react";
import { ChevronDown, Download } from "lucide-react";
import {
  cellToCsvString,
  isFormattedCell,
  sanitizeCellValue,
  type FormattedCell,
} from "../../utils/xlsxHelpers";

export interface ExportColumn<T = unknown> {
  key: string;
  header: string;
  /** Formatter opcional. Tipos suportados:
   * - string  → célula texto.
   * - number  → célula numero (Excel infere).
   * - FormattedCell (NumericCell/DateCell) → célula tipada com numFmt
   *   aplicado (Task #74 — permite SUM/AVG nativo no Excel).
   * - null    → célula vazia.
   */
  format?: (row: T) => string | number | FormattedCell | null;
}

/** Definição de uma planilha extra pra export multi-aba (.xlsx). */
export interface ExtraSheet<U extends Record<string, unknown> = Record<string, unknown>> {
  name: string;
  columns: ExportColumn<U>[];
  rows: U[];
}

interface Props<T extends Record<string, unknown>> {
  filename: string;
  columns: ExportColumn<T>[];
  rows: T[];
  /** Planilhas extras pra .xlsx (Task #49). Quando preenchido, o dropdown
   * adiciona uma 3ª opção "Tudo (Excel)" que gera um workbook com a aba
   * principal + as extras. CSV continua exportando só a aba principal. */
  extraSheets?: ExtraSheet[];
  /** Label da aba principal no .xlsx multi-aba. Default "Dados". */
  primarySheetName?: string;
  /** Label customizado da opção "Tudo (Excel)". Default "Tudo (Excel)". */
  extraSheetsLabel?: string;
}

/** Escapa valor pro CSV (pt-BR default — aspas duplas + escape interno). */
function csvEscape(v: unknown): string {
  if (v === null || v === undefined) return "";
  // FormattedCell (numero/data) = formatacao NOSSA, segura -> nao sanitiza como
  // dado (nao prefixa "-1.234,56"). String de DADO -> sanitiza (formula injection).
  const s = isFormattedCell(v) ? cellToCsvString(v) : sanitizeCellValue(String(v));
  // Quoting estrutural: ; " \n E \r (CR no meio quebraria a linha no Excel).
  if (/[",\n\r;]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildCsv<T extends Record<string, unknown>>(cols: ExportColumn<T>[], rows: T[]): string {
  const head = cols.map((c) => csvEscape(c.header)).join(";");
  const body = rows
    .map((r) => cols.map((c) => csvEscape(c.format ? c.format(r) : r[c.key])).join(";"))
    .join("\n");
  return `${head}\n${body}`;
}

/** Resolve o valor de uma celula pra escrita no .xlsx. FormattedCell vai
 * como objeto SheetJS-compativel ({v, t, z}) — XLSX.utils.aoa_to_sheet
 * preserva o `z` (numFmt) ao detectar essa estrutura, garantindo que
 * Excel renderize com locale BR e que SUM/AVG funcionem (Task #74). */
function resolveXlsxCell<T extends Record<string, unknown>>(
  col: ExportColumn<T>, row: T,
): unknown {
  const raw = col.format ? col.format(row) : row[col.key];
  if (raw === null || raw === undefined) return "";
  // String de DADO -> sanitiza contra formula injection ANTES do aoa_to_sheet
  // (defense-in-depth: nao dependemos do type-'s' do SheetJS). Numero passa intacto.
  if (!isFormattedCell(raw)) return sanitizeCellValue(raw);
  if (raw.__cellType === "numeric") {
    return { v: raw.value, t: "n", z: raw.numFmt };
  }
  // date: SheetJS aceita Date direto + cell.t='d' + z formato.
  return { v: raw.value, t: "d", z: raw.numFmt };
}

/** Exportado (#30): o botão "Baixar Excel" do Fluxo reusa a MESMA montagem
 * de células (FormattedCell → {v,t,z}) sem duplicar o writer. */
export function buildAoa<T extends Record<string, unknown>>(
  cols: ExportColumn<T>[], rows: T[],
): unknown[][] {
  const head = cols.map((c) => c.header);
  const body = rows.map((r) => cols.map((c) => resolveXlsxCell(c, r)));
  return [head, ...body];
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function ExportDropdown<T extends Record<string, unknown>>({
  filename,
  columns,
  rows,
  extraSheets,
  primarySheetName = "Dados",
  extraSheetsLabel = "Tudo (Excel)",
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  async function exportCsv() {
    setOpen(false);
    const csv = buildCsv(columns, rows);
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    downloadBlob(blob, `${filename}.csv`);
  }

  async function exportXlsx() {
    setOpen(false);
    const XLSX = await import("xlsx");
    const ws = XLSX.utils.aoa_to_sheet(buildAoa(columns, rows));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, primarySheetName);
    XLSX.writeFile(wb, `${filename}.xlsx`);
  }

  async function exportXlsxAll() {
    setOpen(false);
    if (!extraSheets || extraSheets.length === 0) return;
    const XLSX = await import("xlsx");
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(buildAoa(columns, rows)),
      primarySheetName,
    );
    for (const s of extraSheets) {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(buildAoa(s.columns, s.rows)),
        s.name,
      );
    }
    XLSX.writeFile(wb, `${filename}-completo.xlsx`);
  }

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 text-xs text-white/70 border border-white/10 rounded-md hover:bg-white/5 hover:text-white transition-colors"
      >
        <Download size={14} />
        Exportar
        <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-40 bg-indra-azul-deep border border-white/10 rounded-md shadow-lg z-20 overflow-hidden">
          <button
            type="button"
            onClick={exportCsv}
            className="w-full text-left px-3 py-2 text-xs text-white/80 hover:bg-white/5"
          >
            CSV (.csv)
          </button>
          <button
            type="button"
            onClick={exportXlsx}
            className="w-full text-left px-3 py-2 text-xs text-white/80 hover:bg-white/5"
          >
            Excel (.xlsx)
          </button>
          {extraSheets && extraSheets.length > 0 && (
            <button
              type="button"
              onClick={exportXlsxAll}
              className="w-full text-left px-3 py-2 text-xs text-white/80 hover:bg-white/5 border-t border-white/10"
            >
              {extraSheetsLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
